import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { isDevAffordanceEnabled } from '../../utils/devMode';
import { apiHfpTestLookup, apiHfpTestProvision } from '../../utils/hfpTestApi';
import { saveHfpTestSession } from './hfpTestSession';

const PRONOUN_OPTIONS = [
  { id: 'they/them', label: 'They / them' },
  { id: 'he/him', label: 'He / him' },
  { id: 'she/her', label: 'She / her' },
  { id: 'ze/zir', label: 'Ze / zir' },
];

const ROLE_OPTIONS = [
  { id: 'school', label: 'School Representative' },
  { id: 'guardian', label: 'Parent / Carer' },
];

// Step indices for the new-tester path. A recognised email short-circuits
// straight to the forms after the EMAIL step.
const STEP = {
  EMAIL: 0,
  ROLE: 1,
  NAME: 2,
  PRONOUNS: 3,
};

/**
 * HfpTestEntry
 * ------------
 * Step-by-step entry for the staging-only HFP test site — one question per
 * screen, mirroring the form wizard. Email (with confirmation) is entered
 * first; a returning tester is taken straight to their forms, a new tester
 * continues through role / child name / pronouns before provisioning.
 */
export default function HfpTestEntry() {
  const navigate = useNavigate();

  const [step, setStep] = useState(STEP.EMAIL);
  const [email, setEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [role, setRole] = useState('');
  const [childFirstName, setChildFirstName] = useState('Alex');
  const [pronouns, setPronouns] = useState('he/him');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const goToForms = useCallback((session) => {
    saveHfpTestSession(session);
    navigate('/rms/hfp-test/dashboard');
  }, [navigate]);

  const back = useCallback(() => {
    setError(null);
    setStep((s) => Math.max(STEP.EMAIL, s - 1));
  }, []);

  const advance = useCallback(async () => {
    if (loading) return; // guard against Enter-key double-submit while a call is in flight
    setError(null);

    if (step === STEP.EMAIL) {
      if (!email.trim()) { setError('Please enter your email.'); return; }
      if (email.trim().toLowerCase() !== confirmEmail.trim().toLowerCase()) {
        setError('Your emails do not match. Please re-enter to confirm.');
        return;
      }
      // Recognised tester → straight to their forms; otherwise collect details.
      setLoading(true);
      try {
        const res = await apiHfpTestLookup({ email: email.trim() });
        if (res.found && res.session) {
          goToForms(res.session);
        } else {
          setStep(STEP.ROLE);
        }
      } catch (err) {
        setError(err.message || 'Something went wrong. Please try again.');
      } finally {
        setLoading(false);
      }
      return;
    }

    if (step === STEP.ROLE) {
      if (!role) { setError('Please choose one.'); return; }
      setStep(STEP.NAME);
      return;
    }

    if (step === STEP.NAME) {
      setStep(STEP.PRONOUNS);
      return;
    }

    if (step === STEP.PRONOUNS) {
      setLoading(true);
      try {
        const res = await apiHfpTestProvision({
          email: email.trim(),
          role,
          childFirstName: childFirstName.trim() || 'Alex',
          pronouns,
        });
        goToForms(res.session);
      } catch (err) {
        setError(err.message || 'Something went wrong. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  }, [loading, step, email, confirmEmail, role, childFirstName, pronouns, goToForms]);

  if (!isDevAffordanceEnabled()) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="max-w-md text-center text-gray-600">
          This page is only available in the testing environment.
        </div>
      </div>
    );
  }

  const isLast = step === STEP.PRONOUNS;

  // One question per step.
  const stepContent = () => {
    switch (step) {
      case STEP.EMAIL:
        return (
          <>
            <h2 className="text-xl font-bold text-gray-900">What&apos;s your email?</h2>
            <label className="block">
              <span className="text-xs font-medium text-gray-700">Email</span>
              <input
                type="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') advance(); }}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                placeholder="you@example.com"
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-700">Confirm email</span>
              <input
                type="email"
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') advance(); }}
                onPaste={(e) => e.preventDefault()}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                placeholder="you@example.com"
              />
            </label>
          </>
        );
      case STEP.ROLE:
        return (
          <>
            <h2 className="text-xl font-bold text-gray-900">Who are you filling these in as?</h2>
            <div className="grid grid-cols-1 gap-2">
              {ROLE_OPTIONS.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setRole(r.id)}
                  className={`text-left px-4 py-3 rounded-lg border-2 text-sm font-medium transition-colors ${
                    role === r.id
                      ? 'border-primary bg-primary/5 text-gray-900'
                      : 'border-gray-200 text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </>
        );
      case STEP.NAME:
        return (
          <>
            <h2 className="text-xl font-bold text-gray-900">What&apos;s the child&apos;s first name?</h2>
            <input
              type="text"
              autoFocus
              value={childFirstName}
              onChange={(e) => setChildFirstName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') advance(); }}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none"
              placeholder="Alex"
            />
          </>
        );
      case STEP.PRONOUNS:
        return (
          <>
            <h2 className="text-xl font-bold text-gray-900">What are the child&apos;s pronouns?</h2>
            <select
              autoFocus
              value={pronouns}
              onChange={(e) => setPronouns(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') advance(); }}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white focus:border-primary focus:ring-1 focus:ring-primary outline-none"
            >
              {PRONOUN_OPTIONS.map((p) => (
                <option key={p.id} value={p.id}>{p.label}</option>
              ))}
            </select>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-white to-secondary/15 flex items-center justify-center p-6">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-xl border border-gray-100 p-8 space-y-6">
        <div key={step} className="space-y-3">
          {stepContent()}
        </div>

        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex items-center gap-3">
          {step > STEP.EMAIL && (
            <button
              type="button"
              onClick={back}
              disabled={loading}
              className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 rounded-lg transition-colors disabled:opacity-50"
            >
              Back
            </button>
          )}
          <button
            type="button"
            onClick={advance}
            disabled={loading}
            className="flex-1 px-4 py-3 rounded-lg bg-primary text-white text-sm font-semibold shadow-sm hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {loading ? 'Please wait…' : (isLast ? 'Start' : 'Next')}
          </button>
        </div>
      </div>
    </div>
  );
}
